import { type RefObject, useEffect } from 'react';

type DefaultOptions = {
    enable?: boolean;
    observeChildren?: boolean;
    observerInit?: IntersectionObserverInit;
};

export type Options<T> = DefaultOptions & {
    ref: RefObject<T>;
    childRefs?: RefObject<T[]>;
};

const defaultOptions: DefaultOptions = {
    enable: true,
    observeChildren: false
};

export function useIntersectionEffect<T extends Element>(
    effectCallback: (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => void,
    options: Options<T>,
    deps: unknown[] = []
) {
    const { ref, childRefs, enable, observeChildren, observerInit } = {
        ...defaultOptions,
        ...options
    };

    useEffect(() => {
        const refElement = ref.current;
        if (!enable || !refElement) return;

        const init = observerInit ?? { root: ref.current, threshold: 1 };
        const observer = new IntersectionObserver((entries) => effectCallback(entries, observer), init);

        if (observeChildren) {
            const { children } = ref.current;
            Array.from(children).forEach((x) => observer.observe(x));
        } else if (childRefs?.current) {
            childRefs.current.forEach((x) => observer.observe(x));
        } else {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, [enable, observeChildren, observerInit, ref, childRefs, effectCallback, ...deps]);
}
